/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { Contact } from '../types/contact.types';
import { useOrganizationStore } from '../stores/useOrganizationStore';
import { GET_CONTACT_DETAILS, GET_CONTACTS } from '../lib/graphql/queries/contacts';

function mapGQLToContact(c: Record<string, any>): Contact {
  return {
    id: c.contactId,
    firstName: c.firstName,
    lastName: c.lastName,
    email: c.email,
    phoneNumber: c.phone,
    phoneStatus: c.phoneStatus,
    email_status: c.emailStatus,
    tags: c.tags ?? [],
    opt_in: c.optIn,
    opt_in_date: c.optInDate ?? undefined,
    call_tester: c.callTester ?? false,
    is_test_contact: c.callTester ?? false,
    status: c.status,
    lifecycleStage: c.lifecycleStage,
    leadSource: c.leadSource,
    score: c.leadScore,
    leadIntent: c.intentLevel,
    recentOutcome: c.recentOutcome,
    claimedLead: c.assignedAgentId ?? undefined,
    lastCallAnswered: c.lastCallAnswered ?? undefined,
    has_ealerts_registered: c.hasEalerts,
    flywheelOptOut: c.flywheelOptOut,
    aiPaused: c.aiPaused ?? false,
    organization_id: undefined,
  } as unknown as Contact;
}

export const useContactsByEmail = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { organization } = useOrganizationStore();
  const apolloClient = useApolloClient();

  const getContactsByEmail = useCallback(
    async (
      email: string,
      phone: string,
      contactId?: string,
      organizationIdFromUrl?: string
    ): Promise<Contact[]> => {
      if (!email && !phone && !contactId) {
        throw new Error('Email, phone number, or contact ID is required');
      }

      const effectiveOrganizationId = organizationIdFromUrl || organization?.id;
      if (!effectiveOrganizationId) {
        throw new Error('No organization ID available');
      }

      setLoading(true);
      setError(null);

      try {
        if (contactId) {
          const { data } = await apolloClient.query({
            query: GET_CONTACT_DETAILS,
            variables: { organizationId: effectiveOrganizationId, contactId },
            fetchPolicy: 'network-only',
          });
          const contact = data?.contact;
          if (!contact) return [];
          return [mapGQLToContact(contact)];
        }

        // Search by email or phone — decode URL-encoded phone chars
        const decodedPhone = phone ? decodeURIComponent(phone) : '';
        const searchTerm = email || decodedPhone;

        const { data } = await apolloClient.query({
          query: GET_CONTACTS,
          variables: {
            organizationId: effectiveOrganizationId,
            search: searchTerm,
            limit: 10,
            offset: 0,
            sortOrder: 'DESC',
          },
          fetchPolicy: 'network-only',
        });

        const contacts: Record<string, any>[] = data?.contacts?.contacts ?? [];
        return contacts.map(mapGQLToContact);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch contacts');
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [organization?.id, apolloClient]
  );

  return {
    getContactsByEmail,
    loading,
    error,
  };
};
